import { computed, type MaybeRef, nextTick, ref, unref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMutation } from '@tanstack/vue-query';
import { isNil, toSafeInteger } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { useFirebaseStore } from 'freedom-shared';
import { extractFirstPlatformError } from 'shared';

import { useBill, useBillOptions } from '@/entities/bill';
import {
  applyLoyaltyCashback,
  cancelLoyaltyCashback,
  useLoyaltyCustomer,
} from '@/entities/loyalty';

export function useLoyaltyCashbackConfig({
  billId,
  enabled,
}: {
  billId: MaybeRef<string | undefined>;
  enabled: MaybeRef<boolean>;
}) {
  const { options: billOptions, refetch } = useBillOptions({
    billId,
    enabled,
  });

  const config = computed(() =>
    billOptions.value?.loyalty.cashbacks
      ? billOptions.value?.loyalty.cashbacks
      : undefined,
  );

  const allowed = computed(() => Boolean(config.value?.usage_allowed));

  const reason = computed(() => config.value?.reason);

  return {
    allowed,
    reason,
    refetch,
  };
}

export function useApplyCashbackMutation({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: unknown) => void;
}) {
  return useMutation({
    mutationFn: ({ billId }: { billId: MaybeRef<string | undefined> }) => {
      const _billId = unref(billId);

      if (isNil(_billId)) {
        return Promise.reject(new Error('ERR_BILL_ID_IS_NIL'));
      }

      return applyLoyaltyCashback({ billId: _billId });
    },
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: unknown) => {
      onError(error);
    },
  });
}

export function useCancelCashbackMutation({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: unknown) => void;
}) {
  return useMutation({
    mutationFn: ({ applicationId }: { applicationId: MaybeRef<string> }) =>
      cancelLoyaltyCashback({ applicationId: unref(applicationId) }),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: unknown) => {
      onError(error);
    },
  });
}

export function useLoyaltyCashback({
  orderId,
  onError,
}: {
  orderId: MaybeRef<string>;
  onError?: (message: string) => void;
}) {
  const { t } = useI18n();

  const { serializedRemoteConfig, isLoading: isConfigLoading } =
    storeToRefs(useFirebaseStore());

  const isLoyaltyCashbackFeatureEnabled = computed(() => {
    const enabledFeatures = new Set(
      (serializedRemoteConfig.value?.avfree_features || []) as string[],
    );
    return enabledFeatures.has('apply_loyalty_cashback');
  });

  const {
    bill,
    isRefetching: isBillRefetching,
    isLoading: isBillLoading,
    refetch: refetchBill,
  } = useBill({
    orderId,
  });

  const {
    cashback: loyaltyCashback,
    isLoading: isCustomerLoading,
    isRefetching: isCustomerRefetching,
    refetch: refetchLoyaltyCustomer,
  } = useLoyaltyCustomer({
    enabled: isLoyaltyCashbackFeatureEnabled,
  });

  const {
    allowed: isLoyaltyCashbackAllowed,
    reason: loyaltyCashbackDisallowedReason,
  } = useLoyaltyCashbackConfig({
    billId: computed(() => bill.value?.id),
    enabled: isLoyaltyCashbackFeatureEnabled,
  });

  const billCashbackDynamicModifier = computed(() =>
    bill.value?.dynamic_modifiers.find(
      modifier => modifier.modifier_type === 'CASHBACK',
    ),
  );

  const applicationId = computed(
    () => billCashbackDynamicModifier.value?.provider_id,
  );

  const appliedAmount = computed(() =>
    Math.abs(toSafeInteger(billCashbackDynamicModifier.value?.amount)),
  );

  const isApplied = computed(
    () =>
      billCashbackDynamicModifier.value?.is_enabled ||
      !isNil(applicationId.value),
  );

  const isToggled = ref(isApplied.value);

  watch(isApplied, () => {
    isToggled.value = isApplied.value;
  });

  const canViewUi = computed(() => {
    if (!isLoyaltyCashbackFeatureEnabled.value) {
      return false;
    }

    const hasAppliedEverything = isApplied.value && loyaltyCashback.value === 0;
    const hasAvailableCashback = loyaltyCashback.value > 0;

    return hasAppliedEverything || hasAvailableCashback;
  });

  const applyMutation = useApplyCashbackMutation({
    onSuccess: () => {
      refetchBill();
      refetchLoyaltyCustomer();
    },
    onError: cause => {
      if (onError) {
        const errorMessage =
          extractFirstPlatformError(cause)?.message ??
          t('loyalty.defaultError');
        onError(errorMessage);
      }

      nextTick(() => (isToggled.value = false));
    },
  });

  const cancelMutation = useCancelCashbackMutation({
    onSuccess: () => {
      refetchBill();
      refetchLoyaltyCustomer();
    },
    onError: cause => {
      if (onError) {
        const errorMessage =
          extractFirstPlatformError(cause)?.message ??
          t('loyalty.defaultError');
        onError(errorMessage);
      }

      nextTick(() => (isToggled.value = true));
    },
  });

  const isApplicationPending = computed(
    () => applyMutation.isLoading.value || cancelMutation.isLoading.value,
  );

  function apply() {
    applyMutation.mutate({ billId: computed(() => bill.value?.id) });
  }

  function cancel() {
    if (applicationId.value) {
      cancelMutation.mutate({ applicationId: applicationId.value });
    }
  }

  function onToggle() {
    if (isToggled.value) {
      isToggled.value = false;
      cancel();
      return;
    }

    isToggled.value = true;
    apply();
  }

  return {
    onToggle,
    canViewUi,
    isApplied,
    isToggled,
    isBillLoading,
    isBillRefetching,
    isConfigLoading,
    isCustomerLoading,
    isCustomerRefetching,
    isApplicationPending,
    isLoyaltyCashbackAllowed,
    appliedAmount,
    loyaltyCashback,
    loyaltyCashbackDisallowedReason,
  };
}
